/**
 * Service Worker startup initialization (module-level execution)
 * Runs BEFORE React components are rendered
 *
 * Handles:
 * - Auto-detection of SW config changes
 * - Switching between sw.js and sw-disable.js
 * - Preventing need for manual hard refresh on config changes
 */

import { isSWAvailable } from '../config';
import { Logger } from '../utils/Logger';

let swUnregisterInitiated = false;

/**
 * Initialize Service Worker configuration based on isSWAvailable setting
 *
 * Behavior:
 * - If isSWAvailable = false: unregister all SWs, register disable worker
 * - If isSWAvailable = true but disable worker active: auto-switch to main SW
 *
 * This runs at module load time (before React renders)
 */
export function initializeSW(): void {
  if (!navigator.serviceWorker || typeof navigator.serviceWorker.getRegistrations !== 'function') {
    return; // Service Worker not supported
  }

  navigator.serviceWorker.getRegistrations().then((registrations) => {
    // Detect if sw-disable.js is registered (check scriptURL, not just scope)
    // All SWs have scope '/', so we must check the script filename
    const hasDisableSW = registrations.some((reg) =>
      reg.active?.scriptURL.includes('sw-disable') ||
      reg.installing?.scriptURL.includes('sw-disable') ||
      reg.waiting?.scriptURL.includes('sw-disable')
    );

    if (!isSWAvailable) {
      // Config says SW disabled: unregister all and register disable worker
      if (registrations.length > 0) {
        Logger.debug('APP', 'STARTUP: SW disabled - unregistering all SWs and registering disable worker');
        swUnregisterInitiated = true;

        registrations.forEach((reg) => {
          Logger.debug('APP', 'STARTUP: Unregistering SW:', reg.scope);
          reg.unregister();
        });

        // Register disable SW immediately as extra safety layer
        navigator.serviceWorker.register('/sw-disable.js', { scope: '/' })
          .then((reg) => {
            Logger.debug('APP', '✅ Disable SW registered - all SWs will be uninstalled');
            return reg.update();
          })
          .catch((error) => {
            Logger.error('APP', 'STARTUP: Failed to register disable SW:', error);
          });
      }
    } else {
      // Config says SW enabled: check if disable worker is active and switch to main SW
      if (hasDisableSW) {
        Logger.debug('APP', 'STARTUP: SW configuration changed from disabled to enabled - switching to main SW');
        registrations.forEach((reg) => {
          Logger.debug('APP', 'STARTUP: Unregistering disable SW:', reg.scope);
          reg.unregister();
        });
        // Main SW will be registered by useRegisterSW hook in AppContent
      }
    }
  }).catch((error) => {
    Logger.error('APP', 'STARTUP: Failed to check Service Worker registrations:', error);
  });
}

/**
 * Get the flag indicating if SW unregister was initiated at startup
 */
export function getSWUnregisterInitiated(): boolean {
  return swUnregisterInitiated;
}
